/**
 * GitLab Service
 *
 * Frontend service for the GitLab issue feature. Calls the backend GitLab
 * proxy (which holds the GitLab token server-side) via the shared API Gateway:
 *   GET  /api/gitlab/assignees → list members assignable to issues
 *   POST /api/gitlab/issues    → create an issue
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A GitLab member who can be assigned to an issue. Mirrors the trimmed shape
 * returned by the backend proxy's GET /api/gitlab/assignees route.
 */
export interface GitlabAssignee {
  /** The GitLab numeric user id, used as the issue's assignee id. */
  id: number
  /** The member's display name. */
  name: string
  /** The member's GitLab username. */
  username: string
}

/**
 * The payload needed to create a GitLab issue through the backend proxy.
 */
export interface CreateGitlabIssueRequest {
  /** The issue title. */
  title: string
  /** The issue description/body (may be empty). */
  description: string
  /** Optional GitLab user id to assign the issue to. */
  assigneeId: number | null
}

/**
 * The result of a successful issue creation, mirroring the backend response.
 */
export interface CreateGitlabIssueResponse {
  /** The GitLab global issue id. */
  id: number
  /** The project-scoped issue id (the number shown in the GitLab UI). */
  iid: number
  /** The web URL of the created issue. */
  webUrl: string
}

/**
 * A GitLab issue in the configured project. Mirrors the shape
 * returned by the backend proxy's GET /api/gitlab/issues route.
 */
export interface GitlabIssue {
  /** The GitLab global issue id. */
  id: number
  /** The project-scoped issue id (the number shown in the GitLab UI). */
  iid: number
  /** The issue title. */
  title: string
  /** The web URL of the issue. */
  webUrl: string
  /** The issue state as reported by GitLab (e.g. "opened", "closed"). */
  state: string
}

// ---------------------------------------------------------------------------
// API URL Resolution
// ---------------------------------------------------------------------------

/** Cached API base URL loaded from aws-exports.json. */
let GITLAB_API_URL: string = ""

/**
 * Load the GitLab API base URL from aws-exports.json configuration.
 *
 * Uses the VITE_API_URL env var if set (local dev with --host), otherwise the
 * apiUrl baked into aws-exports.json for deployed builds, and finally a
 * relative path as a last resort.
 *
 * @returns The base URL for the GitLab API (e.g. "https://host/api/gitlab").
 * @throws Error if the configuration cannot be loaded.
 */
async function loadApiUrl(): Promise<string> {
  if (GITLAB_API_URL) {
    return GITLAB_API_URL
  }

  // VITE_API_URL is available at build time in deployed/dev-host mode.
  const envUrl: string = import.meta.env.VITE_API_URL || ""
  if (envUrl) {
    GITLAB_API_URL = `${envUrl}/api/gitlab`
    return GITLAB_API_URL
  }

  // Fall back to aws-exports.json for CI-built deployments.
  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = config.apiUrl || ""
    GITLAB_API_URL = baseUrl ? `${baseUrl}/api/gitlab` : "/api/gitlab"
    return GITLAB_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("GitLab API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// API Calls
// ---------------------------------------------------------------------------

/**
 * Fetch the list of members who can be assigned to GitLab issues in the
 * configured project.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns An array of assignable GitLab members.
 * @throws Error if the API call fails.
 */
export async function getAssignees(token: string): Promise<GitlabAssignee[]> {
  const apiUrl: string = await loadApiUrl()

  const response = await fetch(`${apiUrl}/assignees`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch GitLab assignees: HTTP ${response.status}`)
  }

  const data: { assignees: GitlabAssignee[] } = await response.json()
  return data.assignees
}

/**
 * Create a GitLab issue in the configured project through the backend proxy.
 *
 * @param token - Auth0 access token for API authentication.
 * @param request - The issue title, description, and optional assignee id.
 * @returns The created issue's ids and web URL.
 * @throws Error if the API call fails.
 */
export async function createIssue(
  token: string,
  request: CreateGitlabIssueRequest,
): Promise<CreateGitlabIssueResponse> {
  const apiUrl: string = await loadApiUrl()

  const response = await fetch(`${apiUrl}/issues`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      title: request.title,
      description: request.description,
      // Send null through as-is; the backend treats a missing/null assignee as
      // "leave unassigned" rather than failing.
      assigneeId: request.assigneeId,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to create GitLab issue: HTTP ${response.status}`)
  }

  const data: CreateGitlabIssueResponse = await response.json()
  return data
}

/** The GitLab issue states the backend list endpoint accepts. */
export type GitlabIssueState = "opened" | "closed" | "all"

/**
 * Fetch issues in the configured GitLab project through the backend proxy,
 * filtered by state. The backend pages through all results, so this returns
 * every matching issue rather than a single page.
 *
 * @param token - Auth0 access token for API authentication.
 * @param state - Which issues to return. Defaults to "opened" so a closed
 *   ticket does not block raising a new one with the same title.
 * @returns An array of the project's issues in the requested state.
 * @throws Error if the API call fails.
 */
export async function listIssues(
  token: string,
  state: GitlabIssueState = "opened",
): Promise<GitlabIssue[]> {
  const apiUrl: string = await loadApiUrl()

  // Base makes this work whether apiUrl is absolute or a relative fallback.
  const url = new URL(`${apiUrl}/issues`, window.location.origin)
  url.searchParams.set("state", state)

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch GitLab issues: HTTP ${response.status}`)
  }

  const data: { issues: GitlabIssue[] } = await response.json()
  return data.issues
}

// ---------------------------------------------------------------------------
// Issue Title Convention
// ---------------------------------------------------------------------------

/**
 * Build the canonical GitLab issue title for a parasite rate validation
 * failure. Both the "raise issue" dialog (when creating) and the cluster error
 * card (when checking whether an issue already exists) must produce byte-for-
 * byte identical titles, so the title lives here as the single source of truth.
 *
 * Missing values fall back to "Unknown" rather than silently producing a
 * malformed title.
 *
 * @param params - The country name and source id identifying the failure group.
 * @param params.country - The failure's country name, or null when absent.
 * @param params.sourceId - The failure's source id (source_id1), or null.
 * @returns The issue title, e.g. "PR Validation Failure: Kenya 12345".
 */
export function buildValidationFailureIssueTitle({
  country,
  sourceId,
}: {
  country: string | null
  sourceId: string | null
}): string {
  return `PR Validation Failure: ${country ?? "Unknown"} ${sourceId ?? "Unknown"}`
}

// ---------------------------------------------------------------------------
// Issues Cache
// ---------------------------------------------------------------------------
//
// The parasite rate UI needs to know, for many clusters at once, whether an
// open GitLab issue already exists for each one (keyed by the issue's title).
// To avoid one network call per cluster, the open issue list is fetched once
// and cached here, keyed by title for O(1) lookup. The cache is refreshed
// after a new issue is created so the UI immediately reflects the new ticket.

/**
 * An in-flight or resolved promise for the title-keyed issue cache.
 */
let issuesCachePromise: Promise<Map<string, GitlabIssue>> | null = null

/**
 * Build a title-keyed map from a flat list of issues.
 *
 * When two issues share a title, the first one encountered wins. Issues are
 * returned by GitLab newest-first, so this keeps the most recent issue for a
 * given title.
 *
 * @param issues - The flat list of issues to index.
 * @returns A map from issue title to the issue.
 */
function indexIssuesByTitle(issues: GitlabIssue[]): Map<string, GitlabIssue> {
  const byTitle: Map<string, GitlabIssue> = new Map<string, GitlabIssue>()
  for (const issue of issues) {
    if (!byTitle.has(issue.title)) {
      byTitle.set(issue.title, issue)
    }
  }
  return byTitle
}

/**
 * Load the title-keyed issue cache, fetching it from the backend on first use
 * and reusing the result on subsequent calls.
 *
 * Callers that need to guarantee fresh data (e.g. right after creating an
 * issue) should call refreshIssuesCache instead.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns A map from issue title to the matching issue.
 * @throws Error if the underlying API call fails. A failed load is not cached,
 *   so a later call can retry.
 */
export async function loadIssuesCache(token: string): Promise<Map<string, GitlabIssue>> {
  if (issuesCachePromise !== null) {
    return issuesCachePromise
  }

  // Store the promise before awaiting so concurrent callers share it. If the
  // fetch rejects, clear the cached promise so a later call can retry rather
  // than being stuck with a permanently rejected promise.
  const loadPromise: Promise<Map<string, GitlabIssue>> = listIssues(token)
    .then((issues) => indexIssuesByTitle(issues))
    .catch((error) => {
      issuesCachePromise = null
      throw error
    })

  issuesCachePromise = loadPromise
  return loadPromise
}

/**
 * Force a reload of the issue cache from the backend, replacing any cached
 * value. Call this after creating an issue so subsequent lookups see it.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The freshly loaded title-keyed issue map.
 * @throws Error if the underlying API call fails.
 */
export async function refreshIssuesCache(token: string): Promise<Map<string, GitlabIssue>> {
  issuesCachePromise = null
  return loadIssuesCache(token)
}

/**
 * Discard the cached issue list so the next loadIssuesCache call re-fetches it.
 * Useful when the frontend knows the cache is stale but does not have a token
 * on hand to refresh immediately.
 *
 * @returns Nothing. Clears module state only.
 */
export function invalidateIssuesCache(): void {
  issuesCachePromise = null
}
